import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Search } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useSEO } from "../hooks/useSEO";
import { Reveal } from "./Reveal";
import { blogList as staticBlogList } from "../lib/blogUtils";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
};

const BlogIndex = () => {
  useSEO({
    title: "Blog | Skope Kitchens",
    description: "Guides and insights on cloud kitchens, restaurant business, food costs, franchising and scaling food brands — from Skope Kitchens.",
    canonical: "https://www.skopekitchens.com/blog",
  });

  // Database-driven listing: published posts come from the CMS API (newest first);
  // the bundled static copy is only an emergency fallback so articles can never silently vanish.
  const [posts, setPosts] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    let live = true;
    fetch(`${API}/api/blog/posts`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (!live) return;
        setPosts(
          d.posts.map((p) => ({
            url: `/blog/${p.slug}`,
            title: p.title,
            description: p.excerpt || "",
            image: p.featuredImage?.src ? p.featuredImage : null,
            category: p.category || "Cloud Kitchens",
            date: p.publishedAt,
          }))
        );
      })
      .catch(() => live && setPosts(staticBlogList));
    return () => { live = false; };
  }, []);

  const categories = useMemo(
    () => [...new Set((posts || []).map((p) => p.category))].sort(),
    [posts]
  );
  const visible = useMemo(
    () =>
      (posts || []).filter(
        (p) =>
          (!category || p.category === category) &&
          (!search || `${p.title} ${p.description}`.toLowerCase().includes(search.toLowerCase()))
      ),
    [posts, category, search]
  );

  return (
    <div className="bg-white min-h-screen text-ink font-body" data-testid="blog-index">
      <Navbar />
      <main className="pb-24">
        <div className="bg-ink bg-blueprint text-white pt-32 lg:pt-40 pb-14 lg:pb-20">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
            <Reveal>
              <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.35em] text-skope uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-skope" /> Skope Blog
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 font-display font-bold text-4xl lg:text-6xl tracking-tight text-white" data-testid="blog-index-h1">
                KITCHEN INTELLIGENCE.
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 text-white/55 max-w-xl leading-relaxed">
                Every guide we've published on cloud kitchens, restaurant economics, food costs,
                franchising and scaling food brands in India.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
                <div className="flex-1 flex items-center gap-3 border border-white/20 bg-white/5 px-4">
                  <Search size={15} className="text-white/40 shrink-0" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search articles…"
                    className="w-full bg-transparent py-3 text-sm text-white placeholder:text-white/30 focus:outline-none"
                    data-testid="blog-search"
                  />
                </div>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="border border-white/20 bg-ink px-4 py-3 text-sm text-white/70 focus:outline-none focus:border-skope"
                  data-testid="blog-category-filter"
                >
                  <option value="">All categories</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 mt-14">
          {posts === null ? (
            <p className="text-ink/40 font-mono text-xs tracking-[0.2em] uppercase">Loading articles…</p>
          ) : visible.length === 0 ? (
            <p className="text-ink/50" data-testid="blog-empty">No articles match your search.</p>
          ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-list">
            {visible.map((b, i) => (
              <Reveal key={b.url} delay={Math.min(i * 0.04, 0.4)}>
                <Link
                  to={b.url}
                  className="group block border border-ink/10 bg-white h-full hover:border-skope/50 transition-colors duration-300"
                  data-testid={`blog-card-${b.url.split("/").pop()}`}
                >
                  <div className="relative aspect-[16/9] overflow-hidden bg-[#f6f6f6]">
                    {b.image ? (
                      <img
                        src={b.image.src}
                        alt={b.image.alt || b.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-full h-full bg-blueprint flex items-center justify-center">
                        <span className="font-mono text-[10px] tracking-[0.3em] text-skope uppercase">Skope Kitchens</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/10 transition-colors duration-500" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-[10px] tracking-[0.25em] text-skope uppercase">{b.category}</span>
                      {b.date && <span className="font-mono text-[10px] text-ink/30">{fmtDate(b.date)}</span>}
                    </div>
                    <h2 className="mt-3 font-display font-semibold text-lg text-ink group-hover:text-skope transition-colors duration-300 leading-snug">
                      {b.title}
                    </h2>
                    {b.description && (
                      <p className="mt-2.5 text-sm text-ink/50 leading-relaxed line-clamp-2">{b.description}</p>
                    )}
                    <span className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 group-hover:text-skope transition-colors">
                      Read Article
                      <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogIndex;
